import { LoraGenderEnum } from "@/enums/lora-gender.enum.ts";
import { LoraStatusEnum } from "@/enums/lora-status.enum.ts";
import useEcho from "@/hooks/use-echo.hook.ts";
import { Check } from "@/icons/Check.tsx";
import { CircleInfo } from "@/icons/CircleInfo.tsx";
import { CircleXMark } from "@/icons/CircleXMark.tsx";
import { FolderUser } from "@/icons/FolderUser.tsx";
import { MagnifyingGlass } from "@/icons/MagnifyingGlass.tsx";
import { Mars } from "@/icons/Mars.tsx";
import { Venus } from "@/icons/Venus.tsx";
import useUserStore from "@/store/user.store.ts";
import { Loading } from "@/ui/shared/Loading.tsx";
import api from "@/lib/api.ts";
import { LoraModel } from "@/models/lora.model.ts";
import { getLoras } from "@/services/lora.service.ts";
import { LoraCard } from "@/ui/shared/LoraCard.tsx";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Text,
  Button,
  Card,
  Center,
  useMantineTheme,
  Alert,
  Modal,
  TextInput,
  Flex,
  Group,
  SegmentedControl,
  CloseButton,
  Title,
  List,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { z, ZodType } from "zod";

type LoraForm = {
  id?: number;
  status: LoraStatusEnum;
  gender: LoraGenderEnum;
  name: string;
  trigger: string;
};

const loraForm: ZodType<LoraForm> = z.object({
  id: z.number().optional(),
  status: z.nativeEnum(LoraStatusEnum),
  gender: z.nativeEnum(LoraGenderEnum),
  name: z.string().min(1, { message: "Name is required" }),
  trigger: z
    .string()
    .min(3, { message: "Character handle must be at least 3 characters" })
    .max(16, { message: "Character handle must be at most 16 characters" })
    .startsWith("@", { message: "Character handle must start with @" })
    .regex(/^[a-zA-Z0-9@]+$/, {
      message:
        "Character handle must contain only letters, numbers and start with @ symbol",
    }),
});

type Filter = "all" | "draft" | "training" | "published";

export function Trainer() {
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const { webSocketsUserId } = useUserStore();
  const [formOpened, { open: openForm, close: closeForm }] =
    useDisclosure(false);
  const [
    confirmationOpened,
    { open: openConfirmation, close: closeConfirmation },
  ] = useDisclosure(false);
  const [activeLora, setActiveLora] = useState<LoraModel | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 400);
  const [filter, setFilter] = useState<Filter>("all");

  const form = useForm<LoraForm>({
    resolver: zodResolver(loraForm),
    defaultValues: {
      name: "",
      trigger: "@",
      status: LoraStatusEnum.draft,
      gender: LoraGenderEnum.male,
    },
  });
  const errors = form.formState.errors;

  const mutation = useMutation({
    mutationFn: (loraForm: LoraForm) => {
      if (loraForm.id) {
        return api.patch(`loras/${loraForm.id}`, {
          ...loraForm,
        });
      }

      return api.post("loras", {
        ...loraForm,
      });
    },
    onSuccess: (response) => {
      notifications.show({
        title: "Success!",
        message: "You can start adding photos to your character",
        color: "green",
      });
      closeForm();
      form.reset();
      navigate(`/characters/${response.data.data.uuid}/uploader`);
    },
    retry: 1,
  });

  const deleteMutation = useMutation({
    mutationFn: (lora: LoraModel) => {
      return api.delete(`/loras/${lora.id}`);
    },
    onSuccess: () => {
      notifications.show({
        title: "Success!",
        message: "The character has been successfully deleted.",
        color: "green",
      });
    },
    onSettled: () => {
      setActiveLora(null);
      refetch();
    },
  });

  const onSubmit: SubmitHandler<LoraForm> = (data) => {
    mutation.mutate(data);
  };

  const params = {
    limit: 0,
    sort: "name",
    "filter[status]": filter,
    "filter[search]": debouncedSearch,
  };

  const { status, data, refetch } = useQuery({
    queryKey: ["loras", params],
    queryFn: () => getLoras(params),
    retry: 1,
  });
  const loras: LoraModel[] = data?.data || [];

  const onEdit = (lora: LoraModel) => {
    form.setValue("id", lora.id);
    form.setValue("name", lora.name);
    form.setValue("trigger", lora.trigger);
    form.setValue("status", lora.status);
    form.setValue("gender", lora.gender);
    if (lora.status !== LoraStatusEnum.draft) {
      //
    }
    openForm();
  };
  const onDelete = (lora: LoraModel) => {
    openConfirmation();
    setActiveLora(lora);
  };

  const echo = useEcho();

  useEffect(() => {
    if (webSocketsUserId && echo) {
      echo.private(`studio.${webSocketsUserId}`).listen("LoraUpdated", () => {
        refetch();
      });
    }
  }, [echo, webSocketsUserId, refetch]);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-y-4 lg:gap-x-4">
        <div className="col-span-1">
          <Card className="flex flex-col shrink gap-4" padding="lg" withBorder>
            <Alert
              bd="1px dashed"
              color="indigo"
              icon={<CircleInfo size={20} />}
            >
              By training a new character you automatically agree to our{" "}
              <a
                className="underline decoration-dashed underline-offset-2"
                target="_blank"
                href="https://photofuse.ai/terms-of-service/"
              >
                Terms of Service
              </a>
              .
            </Alert>

            <Text>Please follow these image upload guidelines:</Text>

            <List spacing="md" size="md" center icon={<Check size={20} />}>
              <List.Item>Avoid harmful or offensive content</List.Item>
              <List.Item>Upload only images you have legal rights to</List.Item>
              <List.Item>
                Obtain consent when training models with people's images
              </List.Item>
            </List>

            <Button onClick={openForm} fullWidth>
              Create new character
            </Button>
          </Card>
        </div>
        <div className="col-span-1 md:col-span-2 lg:col-span-2 xl:col-span-3 2xl:col-span-4 flex flex-col w-full gap-5">
          <div className="flex flex-col md:flex-row mb-4 gap-2 w-full justify-start md:justify-between md:items-center">
            <Title order={3} fw={500}>
              Characters
            </Title>

            <TextInput
              miw={300}
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
              leftSection={<MagnifyingGlass size={14} />}
              placeholder="Search characters by name..."
              rightSection={
                search && (
                  <CloseButton
                    className="cursor-pointer"
                    aria-label="Clear input"
                    onClick={() => setSearch("")}
                  />
                )
              }
            />

            <SegmentedControl
              color="grape"
              value={filter}
              onChange={(value) => setFilter(value as Filter)}
              data={[
                {
                  label: "All",
                  value: "all",
                },
                {
                  label: "Draft",
                  value: "draft",
                },
                {
                  label: "Training",
                  value: "training",
                },
                {
                  label: "Published",
                  value: "published",
                },
              ]}
            />
          </div>

          {status === "pending" && <Loading />}
          {status === "error" && (
            <Alert
              icon={<CircleXMark size={20} />}
              color="red"
              title="Something went wrong."
              mb={10}
            >
              Unable to load your characters. Please try again later.
            </Alert>
          )}

          {status === "success" && loras?.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
              {loras.map((lora) => (
                <LoraCard
                  key={lora.id}
                  lora={lora}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
            </div>
          )}

          {status === "success" && loras?.length === 0 && (
            <Card
              className="flex flex-1 w-100"
              shadow="xs"
              padding="lg"
              withBorder
            >
              <Center>
                <Flex direction="column" gap={15} align="center">
                  <Text size="xl" fw={500}>
                    No characters found
                  </Text>

                  <FolderUser
                    size={80}
                    style={{
                      color: theme.colors.indigo[6],
                    }}
                  />

                  <Text>
                    Currently you have no characters. Start by creating one.
                  </Text>
                </Flex>
              </Center>
            </Card>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-4 w-100"></div>

      <Modal
        opened={confirmationOpened}
        onClose={closeConfirmation}
        title="Are you sure?"
      >
        <div className="flex flex-col gap-4">
          <Alert icon={<CircleInfo size={20} />} color="red">
            This action cannot be undone. Are you sure you want to delete this
            character?
          </Alert>

          <Group justify="end">
            <Button variant="subtle" color="gray" onClick={closeConfirmation}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={() => {
                if (activeLora) {
                  deleteMutation.mutate(activeLora);
                }
                closeConfirmation();
              }}
            >
              Delete
            </Button>
          </Group>
        </div>
      </Modal>

      <Modal
        opened={formOpened}
        onClose={() => {
          form.reset();
          closeForm();
        }}
        title="Character details"
      >
        <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
          <TextInput
            {...form.register("name")}
            type="text"
            name="name"
            withAsterisk
            label="Name"
            size="md"
            error={errors.name?.message}
          />

          <TextInput
            {...form.register("trigger")}
            placeholder="@something"
            disabled={form.getValues("status") === LoraStatusEnum.published}
            type="text"
            name="trigger"
            withAsterisk
            label="Character handle"
            description="This handle will be used to trigger your character"
            size="md"
            error={errors.trigger?.message}
          />

          <Flex direction="column">
            <Text fw={500} component="label" mb={0}>
              Gender
            </Text>

            <SegmentedControl
              defaultValue={form.getValues("gender")}
              onChange={(value) =>
                form.setValue("gender", value as LoraGenderEnum)
              }
              data={[
                {
                  label: (
                    <Center style={{ gap: 5 }}>
                      <Mars />
                      <span>Male</span>
                    </Center>
                  ),
                  value: LoraGenderEnum.male,
                },
                {
                  label: (
                    <Center style={{ gap: 5 }}>
                      <Venus />
                      <span>Female</span>
                    </Center>
                  ),
                  value: LoraGenderEnum.female,
                },
              ]}
            />
          </Flex>

          <Button
            loading={mutation.isPending}
            disabled={mutation.isPending}
            type="submit"
            fullWidth
            size="md"
          >
            {form.getValues("id") ? "Update" : "Create"}
          </Button>
        </form>
      </Modal>
    </>
  );
}
